/**
 * Same-origin value shared with sg-pdc-web-reality (iframe): the iframe reads `_csgb` and sends it as `x-api-key`.
 * Must be the masked key — same algorithm as `sgb-reality-api` `maskPdcApiKey` / sg-pdc-api expectations — not the plain env secret.
 *
 * `VITE_PDC_EMBED_API_KEY` is the plain external API key (same as `pdc.externalApiKey` on the API); we combine it with the app user id before persisting.
 */
export const PDC_EMBED_STORAGE_KEY = '_csgb';

/** Must match `REALITY_PARENT_CLOSE_EMBED_MESSAGE_TYPE` in `sg-pdc-web-reality/src/common/utils/realityParentEmbed.ts`. */
export const PDC_EMBED_CLOSE_MESSAGE_TYPE = 'sgb-reality-close-pdc-course-embed';

/** Must match `REALITY_PARENT_EMBED_CHROME_MESSAGE_TYPE` in sg-pdc-web-reality `realityParentEmbed.ts`. */
export const PDC_EMBED_CHROME_MESSAGE_TYPE = 'sgb-reality-pdc-embed-chrome';

/**
 * Keep in sync with `sgb-reality-api/src/pdc-external/pdc-mask.util.ts` (`maskPdcApiKey`).
 */
export function maskPdcApiKey(userId: number | string, plainKey: string): string {
  const id = typeof userId === "number" ? userId : Number(userId);
  const raw = `${String(id).padStart(8, "0")}.${plainKey}`;
  const b64 = btoa(raw);
  return b64
    .replace(/==/g, 'zKy123')
    .replace(/=/g, 'zKx456')
    .split('')
    .reverse()
    .join('');
}

/**
 * Persists the per-user masked key for the PDC embed iframe (`x-api-key` header).
 */
export function ensurePdcEmbedApiKey(userId: number | string): void {
  const plain = (import.meta.env.VITE_PDC_EMBED_API_KEY as string | undefined)?.trim();
  const id = typeof userId === "number" ? userId : Number(userId);
  if (!plain || !Number.isFinite(id) || id <= 0) {
    return;
  }
  try {
    localStorage.setItem(PDC_EMBED_STORAGE_KEY, maskPdcApiKey(id, plain));
  } catch {
    /* quota / private mode */
  }
}

/** Remove embed key on logout so the next session does not reuse another user's masked key. */
export function clearPdcEmbedStorage(): void {
  try {
    localStorage.removeItem(PDC_EMBED_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
